/**
 * Cuadre del presupuesto: el dinero de las cuentas del presupuesto tiene que
 * estar todo repartido en el presupuesto. Si no, hay un fallo serio (datos
 * rotos, una categoría borrada a medias…), no un descuido del día a día.
 *
 * El saldo es el que ve el usuario: solo movimientos de hoy o antes (ver
 * `hastaHoy` en spreadsheet/bindings). Identidad del presupuesto por sobres de
 * Actual, al céntimo (comprobada contra la hoja real en
 * loot-core/src/server/budget/cuadre.test.ts, con «hoy» antes, dentro y
 * después del mes):
 *
 *   saldo hasta hoy de las cuentas del presupuesto (abiertas y cerradas)
 *     = Listo para asignar del mes
 *     + guardado para el mes siguiente
 *     + disponible de todas las categorías de gasto del mes (ocultas y en
 *       rojo incluidas)
 *     + asignado en los meses siguientes
 *     + movimientos sin categoría de hoy o antes
 *     − movimientos con categoría del mes (o antes) apuntados para días que
 *       aún no han llegado: ya restan del disponible, aún no del saldo
 *     + movimientos con categoría de meses siguientes de hoy o antes (solo
 *       al mirar un mes pasado; en el mes en curso vale 0)
 *
 * Lo sin categoría es lo único que descuadra «a propósito»: ese dinero aún no
 * ha entrado en el presupuesto (y ya hay aviso para categorizarlo).
 *
 * Todo en céntimos. `asignadoFuturo` en positivo; los movimientos, con su
 * signo (gastos en negativo).
 */
export type DatosCuadre = {
  saldoCuentas: number;
  listoParaAsignar: number;
  asignadoFuturo: number;
  guardado: number;
  disponible: number;
  /** Con categoría, del mes o antes, con fecha posterior a hoy. */
  apuntadosFuturos: number;
  /** Con categoría, de meses posteriores al mes, con fecha hasta hoy. */
  mesesSiguientes: number;
  sinCategoria: number;
};

export type ResultadoCuadre = {
  /** Saldo de las cuentas menos lo repartido en el presupuesto. */
  diferencia: number;
  sinCategoria: number;
  /**
   * Lo que no se explica con lo sin categoría: > 0 sobra dinero en las
   * cuentas, < 0 falta.
   */
  descuadre: number;
  cuadra: boolean;
};

/** Lo que el presupuesto dice que hay repartido. */
export function totalPresupuesto(d: DatosCuadre): number {
  return (
    d.listoParaAsignar +
    d.asignadoFuturo +
    d.guardado +
    d.disponible -
    d.apuntadosFuturos +
    d.mesesSiguientes
  );
}

export function calcularCuadre(d: DatosCuadre): ResultadoCuadre {
  const diferencia = d.saldoCuentas - totalPresupuesto(d);
  const descuadre = diferencia - d.sinCategoria;
  return {
    diferencia,
    sinCategoria: d.sinCategoria,
    descuadre,
    cuadra: descuadre === 0,
  };
}
